/**
 * Challenge submissions – one per user per challenge (video + vote count).
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { createChallengeSubmission } from '../../domain/entities/ChallengeSubmission';

const COLLECTION = 'challengeSubmissions';

export function challengeSubmissionRepository({ getFirestore = () => db } = {}) {
  const firestore = getFirestore();
  const coll = () => collection(firestore, COLLECTION);

  return {
    async getById(submissionId) {
      const ref = doc(firestore, COLLECTION, submissionId);
      const snap = await getDoc(ref);
      if (!snap.exists()) return null;
      return createChallengeSubmission({ id: snap.id, ...snap.data() });
    },

    async create(data) {
      // Deterministic doc ID — `${challengeId}_${userId}` — so a second submission
      // attempt by the same user for the same challenge fails atomically at the DB layer
      // rather than relying on a client-side "did we already submit?" check.
      const id = `${data.challengeId}_${data.userId}`;
      const ref = doc(firestore, COLLECTION, id);
      const existing = await getDoc(ref);
      if (existing.exists()) {
        const err = new Error('You already submitted to this challenge.');
        err.code = 'already-exists';
        throw err;
      }
      await setDoc(ref, {
        ...data,
        voteCount: 0,
        createdAt: serverTimestamp(),
      });
      return id;
    },

    async getByChallengeAndUser(challengeId, userId) {
      const q = query(
        coll(),
        where('challengeId', '==', challengeId),
        where('userId', '==', userId),
        limit(1)
      );
      const snap = await getDocs(q);
      if (snap.empty) return null;
      const d = snap.docs[0];
      return createChallengeSubmission({ id: d.id, ...d.data() });
    },

    async listByChallenge(challengeId, limitCount = 100) {
      const q = query(
        coll(),
        where('challengeId', '==', challengeId),
        orderBy('voteCount', 'desc'),
        orderBy('createdAt', 'asc'),
        limit(limitCount)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => createChallengeSubmission({ id: d.id, ...d.data() }));
    },

    subscribeByChallenge(challengeId, limitCount, onUpdate) {
      const q = query(
        coll(),
        where('challengeId', '==', challengeId),
        orderBy('voteCount', 'desc'),
        orderBy('createdAt', 'asc'),
        limit(limitCount)
      );
      return onSnapshot(q, (snap) => {
        const items = snap.docs.map((d) => createChallengeSubmission({ id: d.id, ...d.data() }));
        onUpdate(items);
      });
    },

    async incrementVoteCount(submissionId) {
      const ref = doc(firestore, COLLECTION, submissionId);
      await updateDoc(ref, { voteCount: increment(1) });
    },

    async decrementVoteCount(submissionId) {
      const ref = doc(firestore, COLLECTION, submissionId);
      await updateDoc(ref, { voteCount: increment(-1) });
    },
  };
}

export const challengeSubmissionRepo = challengeSubmissionRepository();
